import { get } from "lodash";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

import WorkspacePropType from "src/custom-prop-types/workspace";
import { isSearchPage } from "src/util/url";
import { matchPaths } from "src/consts/urlPaths";
import { TAGS_UI_COMPONENTS } from "src/util/consts";
import { useGlobalSearchContext } from "./GlobalSearchContext";
import { useGlobalSearchParallel } from "./globalSearch";
import SearchBox from ".";

const SEARCH_COUNT_LIMIT = 3;

const SearchContainer = ({
  onOptionSelect,
  isMulti = false,
  isCloseOnSelect = true,
  includePeople = true,
  includeGroups = true,
  includeTags = true,
  workspace,
  showExploreLink = false,
  tagsVisibleIn = TAGS_UI_COMPONENTS.DIRECTORY,
  Search = SearchBox,
  emptyStateComponent,
}) => {
  const location = useLocation();

  const isForSearchPage = useMemo(() => {
    const match = matchPaths(location.pathname);
    return isSearchPage(match?.path);
  }, [location.pathname]);

  const customer = useMemo(
    () => get(workspace, "config.customer"),
    [workspace]
  );
  const tagTypesConfig = useMemo(
    () => get(workspace, "config.tagConfig"),
    [workspace]
  );

  const {
    defaultSearchScope,
    setDefaultSearchScope,
    peopleFilters,
    groupFilters,
    setPeopleFilters,
    setGroupFilters,
    setDefaultTeamTypes,
    defaultTeamTypes,
    debouncedSearchTerm,
  } = useGlobalSearchContext();

  const { loading, error, results, loadingPeople, loadingTeams, loadingTags } =
    useGlobalSearchParallel({
      disabled: isForSearchPage || !debouncedSearchTerm,
      searchTerm: debouncedSearchTerm,
      size: SEARCH_COUNT_LIMIT,
      scope: defaultSearchScope,
      peopleFilters,
      groupFilters,
      defaultTeamTypes,
      tagTypesConfig,
      tagsVisibleIn,
      includePeople,
      includeGroups,
      includeTags,
      customer,
    });

  // wipe filters when not on search page
  useEffect(() => {
    if (!isForSearchPage) {
      setPeopleFilters((prev) => (prev?.length > 0 ? [] : prev));
      setGroupFilters((prev) => (prev?.length > 0 ? [] : prev));
      setDefaultTeamTypes((prev) => (prev?.length > 0 ? [] : prev));
    }
  }, [
    peopleFilters,
    groupFilters,
    isForSearchPage,
    setPeopleFilters,
    setGroupFilters,
    setDefaultTeamTypes,
  ]);

  const onDefaultScopeClearCallback = useCallback(() => {
    setDefaultSearchScope(null);
  }, [setDefaultSearchScope]);

  return (
    <Search
      isForSearchPage={isForSearchPage}
      isMulti={isMulti}
      isCloseOnSelect={isCloseOnSelect}
      isLoading={loading}
      onOptionSelect={onOptionSelect}
      searchError={error}
      showExploreLink={showExploreLink}
      onDefaultScopeClearCallback={onDefaultScopeClearCallback}
      resultsFormattedData={isForSearchPage ? undefined : results}
      loadingPeople={loadingPeople}
      loadingTeams={loadingTeams}
      loadingTags={loadingTags}
      workspace={workspace}
      emptyStateComponent={emptyStateComponent}
    />
  );
};

SearchContainer.propTypes = {
  onOptionSelect: PropTypes.func,
  isCloseOnSelect: PropTypes.bool,
  isMulti: PropTypes.bool,
  includePeople: PropTypes.bool,
  includeGroups: PropTypes.bool,
  workspace: WorkspacePropType,
  showExploreLink: PropTypes.bool,
  tagsVisibleIn: PropTypes.string,
  includeTags: PropTypes.bool,
  Search: PropTypes.elementType,
  emptyStateComponent: PropTypes.node,
};

// Wrap in react memo so state provider change doesn't re-render component
export default SearchContainer;
